import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    useGetGemsCampaignPerformanceSummaryQuery,
    useGetGemsExchangeRateActiveQuery,
    useGetGemsTopCampaignsQuery,
} from "../../../../redux/apiSlice";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { SubHeader } from "../../Common/layout/SubHeader";
import { formatDate, formatStatus } from "../../../util/Helpers";
import ReactApexChart from "react-apexcharts";
import { Permission } from "../../Common/layout/Permission";
import { Permissions, PermissionType } from "../../../util/Permissions";
import CreateCampaignModal from "./modals/CreateCampaignModal";

const todayStr = new Date().toISOString().split("T")[0];
const formatMoney = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "—";
    return `${currency || "NGN"} ${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const getDurationLabel = (startDate, endDate) => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";
    const days = Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
    const months = Math.round(days / 30);
    if (months >= 1) {
        return `${months} month${months > 1 ? "s" : ""}`;
    }
    return `${days} day${days !== 1 ? "s" : ""}`;
};
const getStatusLabel = (startDate, endDate) => {
    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start && !Number.isNaN(start.getTime()) && start.getTime() > now.getTime()) return "upcoming";
    if (end && !Number.isNaN(end.getTime()) && end.getTime() < now.getTime()) return "completed";
    return "in progress";
};

const GemsCampaigns = () => {
    const { userGroup } = useParams();
    const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
    });

    const commonArgs = useMemo(
        () => ({
            userGroup,
            startDate: filters.startDate || "",
            endDate: filters.endDate || "",
        }),
        [userGroup, filters.startDate, filters.endDate]
    );

    const { data: campaignPerf } = useGetGemsCampaignPerformanceSummaryQuery(commonArgs);
    const { data: topCampaignsData } = useGetGemsTopCampaignsQuery({ ...commonArgs, limit: 10 });
    const { data: activeExchangeRate } = useGetGemsExchangeRateActiveQuery({ userGroup }, { skip: !userGroup });
    const activeRate = activeExchangeRate?.data || activeExchangeRate || null;
    const activeRateValue = Number(activeRate?.rate ?? 0);
    const activeRateCurrency = activeRate?.currency || "NGN";
    const topCampaigns = topCampaignsData?.topCampaigns || [];

    const topCampaignChartSeries = useMemo(
        () => topCampaigns.slice(0, 6).map((c) => Number(c.totalGemsDistributed ?? 0)),
        [topCampaigns]
    );
    const topCampaignChartLabels = useMemo(
        () => topCampaigns.slice(0, 6).map((c) => c.name || c.campaignId),
        [topCampaigns]
    );
    const hasTopCampaignChartData = topCampaignChartSeries.some((v) => v > 0);
    const topCampaignChartOptions = useMemo(
        () => ({
            labels: topCampaignChartLabels,
            legend: { position: "bottom" },
            dataLabels: { enabled: false },
            colors: ["#6993FF", "#1BC5BD", "#FFA800", "#F64E60", "#8950FC", "#00A3FF"],
        }),
        [topCampaignChartLabels]
    );

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <>
            {userGroup && (
                <SubHeader>
                    <SubHeader.Nav>
                        <SubHeader.NavItem label={"Overview"} to={`/gems/${userGroup}/overview`} />
                        <SubHeader.NavItem label={"Accruals"} to={`/gems/${userGroup}/overview/accruals`} />
                        <SubHeader.NavItem label={"Redemptions"} to={`/gems/${userGroup}/overview/redemptions`} />
                        <SubHeader.NavItem label={"Campaigns"} to={`/gems/${userGroup}/overview/campaigns`} />
                        <SubHeader.NavItem
                            label={"Reward attempts"}
                            to={`/gems/${userGroup}/overview/reward-attempts`}
                        />
                        <SubHeader.NavItem label={"Audit logs"} to={`/gems/${userGroup}/overview/audit-logs`} />
                    </SubHeader.Nav>
                </SubHeader>
            )}
            <div className="py-4 px-6">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="font-weight-bolder mb-1">Gems campaigns</h2>
                    <span className="text-muted">
                        Review campaign performance and Gems accrual behaviour for this user group.
                    </span>
                    <div className="d-flex flex-wrap mt-4">
                        <div className="mr-3 mb-2">
                            <label className="text-muted font-size-sm mb-1">Start date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="startDate"
                                value={filters.startDate}
                                onChange={onFilterChange}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="text-muted font-size-sm mb-1">End date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="endDate"
                                value={filters.endDate}
                                onChange={onFilterChange}
                            />
                        </div>
                    </div>
                </div>
                <Permission permission={Permissions.CAMPAIGNS} permissionType={PermissionType.CREATE}>
                    <div className="d-flex align-items-center">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowCreateCampaignModal(true)}
                            disabled={!userGroup}
                        >
                            Create Campaign
                        </button>
                    </div>
                </Permission>
            </div>

                <>
                    <div className="row mb-6">
                        <div className="col-md-12">
                            <Card>
                                <CardHeader title="Top campaigns" />
                                <CardBody>
                                    {hasTopCampaignChartData ? (
                                        <ReactApexChart
                                            options={topCampaignChartOptions}
                                            series={topCampaignChartSeries}
                                            type="donut"
                                            height={260}
                                        />
                                    ) : (
                                        <div className="text-center text-muted py-5">No top campaign data</div>
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    </div>

                    <Card className="mb-6">
                        <CardHeader title="Campaign breakdown" />
                        <CardBody>
                            <div className="table-responsive" style={{ maxHeight: 360, overflow: "auto" }}>
                                <table className="table table-sm table-head-custom table-hover">
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{ width: 50 }}>#</th>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Duration</th>
                                            <th className="text-right">Participation</th>
                                            <th className="text-right">Completions</th>
                                            <th className="text-right" style={{ minWidth: 140 }}>
                                                Completion %
                                            </th>
                                            <th className="text-right" style={{ minWidth: 160 }}>
                                                Total Gems
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topCampaigns.length ? (
                                            topCampaigns.map((c, idx) => {
                                                const completionRate = Number(c.completionRate ?? 0);
                                                const clampedCompletionRate = Math.min(Math.max(completionRate, 0), 100);
                                                const statusLabel = getStatusLabel(c.startDate, c.endDate);
                                                const cashValue =
                                                    Number.isFinite(activeRateValue) && activeRateValue > 0
                                                        ? Number(c.totalGemsDistributed ?? 0) * activeRateValue
                                                        : null;

                                                return (
                                                    <tr key={c.campaignId}>
                                                        <td className="text-muted">{idx + 1}</td>
                                                        <td className="font-weight-bolder">{c.name || c.campaignId}</td>
                                                        <td>
                                                            <span className="label label-light-primary label-inline">
                                                                {c.type || "-"}
                                                            </span>
                                                        </td>
                                                        <td>{formatStatus(statusLabel)}</td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{getDurationLabel(c.startDate, c.endDate)}</span>
                                                                <span className="text-muted font-size-sm">
                                                                    {formatDate(c.startDate)} - {formatDate(c.endDate)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="text-right">
                                                            {Number(c.participationCount ?? 0).toLocaleString()}
                                                        </td>
                                                        <td className="text-right">
                                                            {Number(c.completionCount ?? 0).toLocaleString()}
                                                        </td>
                                                        <td className="text-right">
                                                            <div className="d-flex flex-column align-items-end">
                                                                <div className="text-muted font-size-sm">
                                                                    {completionRate.toFixed(1)}%
                                                                </div>
                                                                <div className="progress w-100" style={{ height: 6 }}>
                                                                    <div
                                                                        className="progress-bar bg-success"
                                                                        role="progressbar"
                                                                        style={{ width: `${clampedCompletionRate}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-right font-weight-bolder">
                                                            <div className="d-flex flex-column align-items-end">
                                                                <span>
                                                                    {Number(c.totalGemsDistributed ?? 0).toLocaleString()}
                                                                </span>
                                                                <span className="text-muted font-size-sm">
                                                                    {cashValue !== null ? formatMoney(activeRateCurrency, cashValue) : "—"}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={9} className="text-center text-muted py-5">
                                                    No campaigns for selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader title="Campaign performance summary" />
                        <CardBody>
                            <div className="table-responsive">
                                <table className="table table-sm table-head-custom">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Duration</th>
                                            <th>Period</th>
                                            <th className="text-right">Participation</th>
                                            <th className="text-right">Completions</th>
                                            <th className="text-right">Completion %</th>
                                            <th className="text-right">Total Gems</th>
                                            <th className="text-right">Avg Gems / user</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaignPerf?.campaigns?.length ? (
                                            campaignPerf.campaigns
                                                .slice()
                                                .sort((a, b) => {
                                                    const aStatus = getStatusLabel(a.startDate, a.endDate);
                                                    const bStatus = getStatusLabel(b.startDate, b.endDate);
                                                    const order = { "in progress": 0, upcoming: 1, completed: 2 };
                                                    return (order[aStatus] ?? 99) - (order[bStatus] ?? 99);
                                                })
                                                .map((c) => {
                                                    const statusLabel = getStatusLabel(c.startDate, c.endDate);
                                                    const totalCash =
                                                        Number.isFinite(activeRateValue) && activeRateValue > 0
                                                            ? Number(c.totalGemsDistributed ?? 0) * activeRateValue
                                                            : null;
                                                    const avgCash =
                                                        Number.isFinite(activeRateValue) && activeRateValue > 0
                                                            ? Number(c.avgGemsPerUser ?? 0) * activeRateValue
                                                            : null;
                                                    return (
                                                        <tr key={c.campaignId}>
                                                            <td>{c.name || c.campaignId}</td>
                                                            <td>{c.type}</td>
                                                            <td>{formatStatus(statusLabel)}</td>
                                                            <td>{getDurationLabel(c.startDate, c.endDate)}</td>
                                                            <td className="text-muted">
                                                                {formatDate(c.startDate)} - {formatDate(c.endDate)}
                                                            </td>
                                                            <td className="text-right">
                                                                {Number(c.participationCount ?? 0).toLocaleString()}
                                                            </td>
                                                            <td className="text-right">
                                                                {Number(c.completionCount ?? 0).toLocaleString()}
                                                            </td>
                                                            <td className="text-right">
                                                                {Number(c.completionRate ?? 0).toFixed(1)}%
                                                            </td>
                                                            <td className="text-right">
                                                                <div className="d-flex flex-column align-items-end">
                                                                    <span>
                                                                        {Number(c.totalGemsDistributed ?? 0).toLocaleString()}
                                                                    </span>
                                                                    <span className="text-muted font-size-sm">
                                                                        {totalCash !== null ? formatMoney(activeRateCurrency, totalCash) : "—"}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="text-right">
                                                                <div className="d-flex flex-column align-items-end">
                                                                    <span>{Number(c.avgGemsPerUser ?? 0).toLocaleString()}</span>
                                                                    <span className="text-muted font-size-sm">
                                                                        {avgCash !== null ? formatMoney(activeRateCurrency, avgCash) : "—"}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                        ) : (
                                            <tr>
                                                <td colSpan={10} className="text-center text-muted py-5">
                                                    No campaign data for selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                </>
            </div>
            <CreateCampaignModal
                show={showCreateCampaignModal}
                onHide={() => setShowCreateCampaignModal(false)}
                userGroup={userGroup}
            />
        </>
    );
};

export default GemsCampaigns;
